import { Api } from "services/api/api.service";
import { ScheduleDispatch } from "./scheduleStore";
import {
  SchedulePreferences,
  scheduleInitState,
} from "./scheduleStore.reducer";
import { ScheduleShiftItem } from "web-apps/company/components/schedule/types/schedule.types";
import { ScheduleSortBy } from "services/api/company/schedule.service";
import type { Result } from "utils/result";
import {
  loadStoredPreferencesWithRecovery,
  parseStoredPreferences,
} from "web-apps/company/stores/preferences.storage.logic";

let requestCounter = 0;
let activeRequestIdRef: number | null = null;
interface InFlightScheduleRequest {
  requestId: number;
  promise: Promise<void>;
}

const inFlightScheduleRequests = new Map<string, InFlightScheduleRequest>();

const schedulePreferencesStorageKey = (companyId: number): string =>
  `PERSISTED::SchedulePreferences::${companyId}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isGroupByOption = (
  value: unknown,
): value is SchedulePreferences["groupByOption"] =>
  value === "person" || value === "team";

const isIntervalOption = (
  value: unknown,
): value is SchedulePreferences["intervalOption"] =>
  value === "weekly" || value === "monthly";

const isSizeOption = (
  value: unknown,
): value is SchedulePreferences["sizeOption"] =>
  value === "large" || value === "small";

const parseSchedulePreferences = (
  raw: string,
): Result<SchedulePreferences, "invalid-preferences"> =>
  parseStoredPreferences(raw, isSchedulePreferences);

const isSchedulePreferences = (
  value: unknown,
): value is SchedulePreferences => {
  if (!isRecord(value)) return false;

  const groupByOption = value.groupByOption;
  const intervalOption = value.intervalOption;
  const sizeOption = value.sizeOption;

  return (
    isGroupByOption(groupByOption) &&
    isIntervalOption(intervalOption) &&
    isSizeOption(sizeOption)
  );
};

const toStorageError = (error: unknown, fallbackMessage: string): Error =>
  error instanceof Error ? error : new Error(fallbackMessage);

const serializeNumberList = (values: number[] | undefined): string =>
  values && values.length > 0 ? values.join(",") : "";

const toScheduleRequestKey = (
  companyId: number,
  fromDate: string,
  toDate: string,
  page: number,
  pageSize: number,
  sortBy: ScheduleSortBy | undefined,
  workGroupIds: number[] | undefined,
  workerIds: number[] | undefined,
): string => {
  return [
    companyId,
    fromDate,
    toDate,
    page,
    pageSize,
    sortBy ?? "",
    serializeNumberList(workGroupIds),
    serializeNumberList(workerIds),
  ].join("|");
};

const getSchedule =
  (dispatch: ScheduleDispatch) =>
  async (
    companyId: number,
    fromDate: string,
    toDate: string,
    page: number,
    pageSize: number,
    sortBy?: ScheduleSortBy,
    workGroupIds?: number[],
    workerIds?: number[],
  ) => {
    const requestKey = toScheduleRequestKey(
      companyId,
      fromDate,
      toDate,
      page,
      pageSize,
      sortBy,
      workGroupIds,
      workerIds,
    );
    const inFlightRequest = inFlightScheduleRequests.get(requestKey);
    if (inFlightRequest) {
      if (
        activeRequestIdRef === null ||
        activeRequestIdRef === inFlightRequest.requestId
      ) {
        activeRequestIdRef = inFlightRequest.requestId;
        dispatch({
          type: "SET_ACTIVE_REQUEST_ID",
          payload: inFlightRequest.requestId,
        });
        dispatch({
          type: "SET_IS_LOADING",
          payload: { value: page === 0, requestId: inFlightRequest.requestId },
        });
        dispatch({
          type: "SET_IS_LOADING_MORE",
          payload: { value: page > 0, requestId: inFlightRequest.requestId },
        });
      }
      return inFlightRequest.promise;
    }

    const requestId = (requestCounter += 1);
    activeRequestIdRef = requestId;
    dispatch({ type: "SET_ACTIVE_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_IS_LOADING",
      payload: { value: page === 0, requestId },
    });
    dispatch({
      type: "SET_IS_LOADING_MORE",
      payload: { value: page > 0, requestId },
    });
    dispatch({ type: "SET_REQUEST_ERROR", payload: null });

    const request = (async (): Promise<void> => {
      try {
        const res = await Api().company.schedule.getSchedule(
          companyId,
          fromDate,
          toDate,
          page,
          pageSize,
          sortBy,
          workGroupIds,
          workerIds,
        );
        dispatch({
          type: "SET_SCHEDULE",
          payload: {
            data: res.data,
            requestId,
            append: page > 0,
            page,
            pageSize,
          },
        });
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Unknown schedule error");
        dispatch({
          type: "SET_REQUEST_ERROR",
          payload: { message: "", err: error },
        });
      } finally {
        dispatch({
          type: "SET_IS_LOADING",
          payload: { value: false, requestId },
        });
        dispatch({
          type: "SET_IS_LOADING_MORE",
          payload: { value: false, requestId },
        });
        inFlightScheduleRequests.delete(requestKey);
      }
    })();

    inFlightScheduleRequests.set(requestKey, { requestId, promise: request });
    return await request;
  };

const setSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (preferences: SchedulePreferences): void => {
    dispatch({ type: "SET_PREFERENCES", payload: preferences });
  };

const patchSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (preferences: Partial<SchedulePreferences>): void => {
    dispatch({ type: "PATCH_PREFERENCES", payload: preferences });
  };

const loadSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (companyId: number): void => {
    loadStoredPreferencesWithRecovery({
      companyId,
      storageKey: schedulePreferencesStorageKey,
      fallback: scheduleInitState.preferences,
      parse: parseSchedulePreferences,
      setState: (next: SchedulePreferences) => {
        dispatch({ type: "SET_PREFERENCES", payload: next });
      },
      setError: (error: Error) => {
        dispatch({
          type: "SET_REQUEST_ERROR",
          payload: {
            message: "Could not load schedule preferences",
            err: toStorageError(error, "Failed to load schedule preferences"),
          },
        });
      },
    });
  };

const persistSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (companyId: number, preferences: SchedulePreferences): void => {
    try {
      localStorage.setItem(
        schedulePreferencesStorageKey(companyId),
        JSON.stringify(preferences),
      );
    } catch (error: unknown) {
      dispatch({
        type: "SET_REQUEST_ERROR",
        payload: {
          message: "Could not persist schedule preferences",
          err: toStorageError(error, "Failed to persist schedule preferences"),
        },
      });
      return;
    }
  };

export const scheduleActions = (dispatch: ScheduleDispatch) => ({
  getSchedule: getSchedule(dispatch),
  resetSchedule: () => {
    activeRequestIdRef = null;
    dispatch({ type: "RESET_SCHEDULE" });
  },
  updateShift: (gigShiftId: number, patch: Partial<ScheduleShiftItem>) =>
    dispatch({ type: "UPDATE_SHIFT", payload: { gigShiftId, patch } }),
  setSchedulePreferences: setSchedulePreferences(dispatch),
  patchSchedulePreferences: patchSchedulePreferences(dispatch),
  loadSchedulePreferences: loadSchedulePreferences(dispatch),
  persistSchedulePreferences: persistSchedulePreferences(dispatch),
});
